import React, { useState } from "react";
import {
  FilterIcon,
  CheckCircleIcon,
  XCircleIcon,
  MailIcon,
  UserIcon,
  CalendarIcon,
  TagIcon,
  BarChartIcon,
  ExternalLinkIcon,
  PlusIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { LoadingFallback } from "@/components/loading-fallback";
import { cn } from "@/lib/utils";
import { MyAdminAccountDropdown } from "@/components/my-admin-account-dropdown";
import moment from "moment";
import { useNavigate } from "react-router";

type EmailCampaignStatus = 'SENT' | 'FAILED' | 'PENDING';
type EmailCampaignRecipientType = 'ALL' | 'SELECTED';

interface EmailCampaign {
  id: number;
  from: string;
  subject: string;
  extraText?: string;
  content: string;
  recipients?: any; // JSON type from schema
  status: EmailCampaignStatus;
  recipientType: EmailCampaignRecipientType;
  createdAt: string;
  updatedAt: string;
}

const statusConfig: Record<EmailCampaignStatus, { name: string; color: string }> = {
  SENT: {
    name: "Sent",
    color: "bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100",
  },
  FAILED: {
    name: "Failed",
    color: "bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100",
  },
  PENDING: {
    name: "Pending",
    color:
      "bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100",
  },
};

// Mock data
const mockEmails = {
  items: [
    {
      id: 1,
      from: "noreply@example.com",
      subject: "Welcome to Our Platform",
      content: "Welcome email content",
      recipientType: "ALL" as EmailCampaignRecipientType,
      status: "SENT" as EmailCampaignStatus,
      createdAt: "2024-01-20T10:00:00Z",
      updatedAt: "2024-01-20T10:00:00Z"
    },
    {
      id: 2, 
      from: "noreply@example.com",
      subject: "Your Monthly Analytics Report",
      content: "Analytics report content",
      recipientType: "SELECTED" as EmailCampaignRecipientType,
      recipients: ["sarah@example.com", "john@example.com"],
      status: "SENT" as EmailCampaignStatus,
      createdAt: "2024-01-19T15:30:00Z",
      updatedAt: "2024-01-19T15:30:00Z"
    },
    {
      id: 3,
      from: "noreply@example.com", 
      subject: "Special Offer Inside!",
      content: "Special offer content",
      recipientType: "ALL" as EmailCampaignRecipientType,
      status: "FAILED" as EmailCampaignStatus,
      createdAt: "2024-01-18T09:15:00Z",
      updatedAt: "2024-01-18T09:15:00Z"
    },
  ],
  totalCount: 3,
  currentPage: 1,
  totalPages: 1,
};

export const ListEmails = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const pageSize = 20;
  const navigate = useNavigate();
  // Mock loading state
  const isLoading = false;
  const error = null;

  return (
    <div className="min-h-screen flex-1 flex flex-col overflow-hidden">
      <main className="flex-1 bg-background overflow-y-auto min-h-screen">
        <header className="border-b pt-6 pb-2 shadow-sm h-auto flex flex-wrap items-center gap-4 px-4 md:px-6">
          <div className="flex items-center gap-4 w-full md:w-auto">
            <h2 className="text-xl font-semibold text-foreground">
              Marketing Emails
              {mockEmails && ` (${mockEmails.totalCount})`}
            </h2>
            <Button
              onClick={() => navigate('/emailClient/create')}
              variant="secondary"
              size="sm"
              className="bg-blue-500/20 text-blue-500 flex items-center gap-2"
            >
              <PlusIcon size={14} />
              <span>New Email</span>
            </Button>
            <Dialog open={isFilterOpen} onOpenChange={setIsFilterOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <FilterIcon size={14} />
                  <span>Filters</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>Filter Emails</DialogTitle>
                </DialogHeader>
                {/* Add filter content here */}
              </DialogContent>
            </Dialog>
          </div>
          <div className="flex items-center space-x-2 w-full md:w-auto md:ml-auto">
            <MyAdminAccountDropdown />
          </div>
        </header>

        <div className="mt-6 px-6">
          <LoadingFallback loading={isLoading}>
            {error ? (
              <div className="text-red-500 font-semibold">Error: {error}</div>
            ) : (
              <>
                <div className="bg-background rounded-xl border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="font-semibold text-foreground/70 w-[30%]">
                          Subject
                        </TableHead>
                        <TableHead className="font-semibold text-foreground/70 w-[20%]">
                          Recipients
                        </TableHead>
                        <TableHead className="font-semibold text-foreground/70 w-[10%]">
                          Status
                        </TableHead>
                        <TableHead className="font-semibold text-foreground/70 w-[20%]">
                          Created At
                        </TableHead>
                        <TableHead className="font-semibold text-foreground/70 w-[20%]">
                          Updated At
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {mockEmails.items.map((email) => (
                        <TableRow
                          onClick={() => navigate(`/emailClient/edit/${email.id}`)}
                          key={email.id}
                          className="hover:bg-muted/50 transition-colors cursor-pointer"
                        >
                          <TableCell className="font-medium">
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-2">
                                <MailIcon
                                  size={16}
                                  className="text-muted-foreground"
                                />
                                {email.subject}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                From: {email.from}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {email.recipientType === 'ALL' ? (
                                <span>All Recipients</span>
                              ) : (
                                <div className="text-blue-500 flex items-center gap-2 underline cursor-pointer">
                                  {email.recipients?.length || 0} Selected
                                  <ExternalLinkIcon size={16} />
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                statusConfig[email.status].color
                              }`}
                            >
                              {statusConfig[email.status].name}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <CalendarIcon
                                size={16}
                                className="text-muted-foreground"
                              />
                              {moment(email.createdAt).format(
                                "MMM D, YYYY h:mm A"
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <CalendarIcon
                                size={16}
                                className="text-muted-foreground"
                              />
                              {moment(email.updatedAt).format(
                                "MMM D, YYYY h:mm A"
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {mockEmails && mockEmails.totalPages > 0 && (
                  <Pagination className="my-8 flex justify-center">
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          onClick={() =>
                            setCurrentPage((prev) => Math.max(prev - 1, 1))
                          }
                          className="cursor-pointer rounded-full bg-background border hover:bg-muted/50 transition-colors"
                        />
                      </PaginationItem>
                      {Array.from(
                        { length: mockEmails.totalPages },
                        (_, i) => i + 1
                      ).map((page) => (
                        <PaginationItem key={page}>
                          <PaginationLink
                            onClick={() => setCurrentPage(page)}
                            isActive={page === mockEmails.currentPage}
                            className={cn(
                              "rounded-full cursor-pointer",
                              page === mockEmails.currentPage
                                ? "bg-blue-500/50"
                                : "bg-background hover:bg-muted/50",
                              "border transition-colors"
                            )}
                          >
                            {page}
                          </PaginationLink>
                        </PaginationItem>
                      ))}
                      <PaginationItem>
                        <PaginationNext
                          onClick={() => setCurrentPage((prev) => prev + 1)}
                          className="cursor-pointer rounded-full bg-background border hover:bg-muted/50 transition-colors"
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                )}
              </>
            )}
          </LoadingFallback>
        </div>
      </main>
    </div>
  );
};
